import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import { SOFTLINE_API_MASTERDATA_ENTITY_QUERY } from '@softapps/core';
import {
  ConstructionSiteCountry,
  ConstructionSiteLookupPage,
  ConstructionSiteRepresentative,
} from '../types/construction-site-lookup';

export const CONSTRUCTION_SITE_LOOKUP_PAGE_SIZE = 50;

interface LookupQueryBody {
  searchTerm: string;
  limit: number;
  offset: number;
  filter?: { type: string } & Record<string, unknown>;
}

export interface ConstructionSiteLookupOptions {
  limit?: number;
  offset?: number;
}

/**
 * The two masterdata lookups the construction-site form needs, queried directly
 * against the masterdata `/query` endpoints.
 *
 * Deliberately not built on `@softapps/core/masterdata`: that library imports the
 * customer side panel from here, so depending on it would close a cycle between
 * the two libraries.
 */
@Injectable({ providedIn: 'root' })
export class ConstructionSiteLookupRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  /** Active representatives only — an inactive one is not offered for assignment. */
  searchRepresentatives(
    searchTerm: string | null | undefined,
    options: ConstructionSiteLookupOptions = {}
  ): Promise<ConstructionSiteLookupPage<ConstructionSiteRepresentative>> {
    return this.query<ConstructionSiteRepresentative>(
      'representative',
      'representatives',
      searchTerm,
      options,
      { type: 'booleanIsTrue', name: 'active' }
    );
  }

  /** The country endpoint rejects a `filter`, so none is sent. */
  searchCountries(
    searchTerm: string | null | undefined,
    options: ConstructionSiteLookupOptions = {}
  ): Promise<ConstructionSiteLookupPage<ConstructionSiteCountry>> {
    return this.query<ConstructionSiteCountry>(
      'country',
      'countries',
      searchTerm,
      options
    );
  }

  /** `searchTerm` is always sent as a string — an explicit `null` answers 400. */
  private async query<T>(
    entity: string,
    responseKey: string,
    searchTerm: string | null | undefined,
    options: ConstructionSiteLookupOptions,
    filter?: { type: string } & Record<string, unknown>
  ): Promise<ConstructionSiteLookupPage<T>> {
    const body: LookupQueryBody = {
      searchTerm: (searchTerm ?? '').trim(),
      limit: options.limit ?? CONSTRUCTION_SITE_LOOKUP_PAGE_SIZE,
      offset: options.offset ?? 0,
    };
    if (filter) body.filter = filter;
    const response = await this.dataAccess.get<Record<string, unknown>>(
      { path: SOFTLINE_API_MASTERDATA_ENTITY_QUERY, pathParams: { entity } },
      body
    );
    const items = (response?.[responseKey] as T[]) ?? [];
    const totalCount = response?.['totalCount'];
    return {
      items,
      totalCount: typeof totalCount === 'number' ? totalCount : items.length,
    };
  }
}
